import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import {
  AnalyticsEventName,
  type AnalyticsEvent,
  type Order,
  type OrderSummary,
} from '@nucleus-commerce/api-client/admin';

import { orderListQueryOptions, orderQueryOptions } from '@/features/orders';

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL spells them, for the route's search schema and loader. `occurredAt`
 * reaches the API as `from` / `to` (see `eventListParams`); the enum `options` drop unknown
 * names the same way the page's filters do, so the prefetch and the page share a query key.
 */
export const EVENT_FILTER_FIELDS = [
  { key: 'name', type: 'enum', options: valuesOf(AnalyticsEventName) },
  { key: 'occurredAt', type: 'date-range' },
  { key: 'sessionId', type: 'text' },
  { key: 'orderId', type: 'async' },
] as const;

const orderOption = (order: Order | OrderSummary): FilterOption => ({
  value: order.id,
  label: order.name,
  description: order.email ?? undefined,
});

export interface EventFiltersOptions {
  /** The `analytics` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of the event log, in the order the filter menu lists them. */
export function eventFilters({
  t,
  queryClient,
  storeId,
}: EventFiltersOptions): Array<FilterDef<AnalyticsEvent>> {
  return [
    {
      key: 'name',
      label: t('report.filters.name'),
      type: 'enum',
      pinned: true,
      aliases: ['event'],
      options: valuesOf(AnalyticsEventName).map(({ value }) => ({
        value,
        label: t(`eventNames.${value}`),
        description: value,
      })),
    },
    {
      key: 'occurredAt',
      label: t('report.filters.occurredAt'),
      type: 'date-range',
      pinned: true,
      aliases: ['time', 'occurred'],
    },
    {
      key: 'sessionId',
      label: t('report.filters.sessionId'),
      type: 'text',
      // The API matches the whole session id, not part of it.
      placeholder: t('report.filters.sessionIdPlaceholder'),
      aliases: ['session'],
    },
    {
      key: 'orderId',
      label: t('report.filters.orderId'),
      type: 'async',
      // The endpoint takes one order.
      multiple: false,
      aliases: ['order'],
      ...queryFilterOptions(queryClient, {
        search: (q) => orderListQueryOptions(storeId, { ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(orderOption),
        resolve: {
          query: ([id]) => orderQueryOptions(storeId, id ?? ''),
          toOptions: (order) => [orderOption(order)],
        },
      }),
    },
  ];
}
